import React from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import SVG from 'components/base/SVG';
import Tooltip from 'app/components/Tooltip';

import { formatPhone } from 'data/investors/investorUtils';
import useSkipTraceInvestors from '../useSkipTraceInvestors';
import useDialInvestorPhone from '../useDialInvestorPhone';
import EditContactModal from './EditContactModal';
import { formatMailingAddress } from './format';
import css from './style.scss';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Contact Information card — Figma "contact info in investor detail",
// Property 1=Not skip traced (300:10872) and the skip-traced variants
// (300:10871 / 350:13651 / 316:12095), inside the Investor Profile card.
//
// The Skip Trace CTA mirrors ../Results/InvestorCard's contact block exactly
// (same copy, same icon) and is left unwired for the same reason that one is:
// S9 (ATAI-102) owns the real skip trace action and is still "ready for dev".
//
// The header's pencil "Edit" button (Figma's "customized contact" variants,
// e.g. 350:13651) opens ./EditContactModal, which persists edits via the
// classic contact save endpoint (ATAI-115) — `onContactSaved` (passed
// through as EditContactModal's `onSaved`) lets the caller refresh the
// profile once a save completes. The mailing-address Verified/Unverified
// badge is still skipped outright: the S5 ticket explicitly defers it to
// Phase 3.
//
// Icon is `cp-edit`, not the sprite's `iconEditInvestor` pencil — that one
// hardcodes fill="#128292" on its paths, which blocks the CSS `color`
// override .editButton__icon needs for the Figma hover/click state (paths
// with an explicit fill attribute don't inherit through <use>). `cp-edit`
// uses fill="currentColor" like Property Tags' icons, so it recolors on
// hover/active the same way MangePropertyTagModal's .deleteIcon does.
//
// One card per vendor-returned `InvestorContact` (ATAI-109's
// `investor.contacts`, merged in by Profile/index.js) — a phone icon row
// (numbers comma-joined, each Do-Not-Call number carrying its own inline
// badge — this card's own per-row source/date badge never had real data to
// show, since the DTO carries no such field) and an email icon row below
// it. The vendor rarely returns a name for these, so a contact with none
// falls back to "Contact N" by position rather than rendering blank.
//
// Icons are `cp-call`/`cp-mail`, matching the existing precedent for a
// contact's phone/email row — `Contacts/ContactProfile/ContactInfoCard.js`.
// They hardcode fill="#128292" (teal), same as `iconEditInvestor`/`cp-plus`/
// `cp-trash` elsewhere in this profile — but here that's the actual target
// color (per that precedent), not something to work around, so no
// currentColor-based swap is needed the way the edit/delete buttons needed
// one for their hover states.
// Sized per Figma's own two icons rather than one shared size — "Call Icon"
// (316:12100) is 17px, "mail" (316:12105) is 20px.
//
// Each phone number is its own underlined span (Figma: the underline sits on
// the number, not on the ", " joining the next one), and the DNC flag (Figma
// "DNC Tag", 6:3508) is an outlined pill with no fill — not the filled pink
// background this used to render.
// Exported so Results/InvestorCard.js (the search-card equivalent of this
// same "skip traced" contact display) can render identically instead of
// duplicating the phone/email/DNC formatting.
//
// Each phone/email carries its own `source` ("SKIP_TRACE" | "MANUAL" | "VENDOR")
// and `date` (ISO yyyy-MM-dd) from propstream-be's contact_phones/contact_emails
// (append_match_*_id set vs null) -- a manually-added number and a skip-traced
// one can both be present on the same investor. Each gets a hover hint
// ("User added [date]" / "Skip Traced [date]", ATAI-422) -- hover only, since
// an always-visible badge on every number was noisy.
const formatSourceDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
};

const SOURCE_LABELS = { MANUAL: 'User added', SKIP_TRACE: 'Skip Traced' };

const SourceHint = ({ source, date, children }) => {
  const label = SOURCE_LABELS[source];
  const formatted = label ? formatSourceDate(date) : null;
  if (!formatted) return children;

  return (
    // Tooltip's "top" position only ever subtracts a fixed 10px from the
    // trigger's own top edge (see its updatePos) -- every consumer in this
    // codebase hand-tunes yOffset to actually clear the trigger; -42 lifts
    // this short single-line pill clear of the phone/email text it wraps.
    <Tooltip content={`${label} ${formatted}`} position="top" arrowOffset="50%" yOffset={-42}>
      {children}
    </Tooltip>
  );
};

// `onPhoneClick`, when passed, makes each number dial through the shared
// ClickToDial flow -- same clickable-span pattern as
// Contacts/ContactProfile/ContactInfoCard's `onDialPhone`. Without it the
// numbers render exactly as before.
export const ContactPerson = ({ contact, index, onPhoneClick }) => {
  const phones = contact.phones || [];
  const emails = contact.emails || [];

  return (
    <div className={css.contactPerson}>
      <p className={css.contactPerson__name}>{contact.name || `Contact ${index + 1}`}</p>

      {!phones.length ? null : (
        <div className={css.contactPerson__row}>
          <SVG
            icon="cp-call"
            className={classNames(css.contactPerson__icon, css['contactPerson__icon--phone'])}
          />
          <span className={css.contactPerson__value}>
            {phones.map((phone, i) => (
              <React.Fragment key={`${phone.number}-${i}`}>
                {i > 0 ? ', ' : ''}
                <SourceHint source={phone.source} date={phone.date}>
                  <span
                    className={classNames(css.contactPerson__phoneNumber, {
                      [css['contactPerson__phoneNumber--dial']]: onPhoneClick,
                    })}
                    onClick={onPhoneClick ? () => onPhoneClick(phone) : undefined}
                  >
                    {formatPhone(phone.number)}
                  </span>
                </SourceHint>
                {!phone.dnc ? null : <span className={css.dncBadge}>DNC</span>}
              </React.Fragment>
            ))}
          </span>
        </div>
      )}

      {!emails.length ? null : (
        <div className={css.contactPerson__row}>
          <SVG
            icon="cp-mail"
            className={classNames(css.contactPerson__icon, css['contactPerson__icon--mail'])}
          />
          <span className={css.contactPerson__value}>
            {emails.map((email, i) => (
              <React.Fragment key={`${email.email}-${i}`}>
                {i > 0 ? ', ' : ''}
                <SourceHint source={email.source} date={email.date}>
                  {email.email}
                </SourceHint>
              </React.Fragment>
            ))}
          </span>
        </div>
      )}
    </div>
  );
};

const ContactInfo = ({ investor, contactId, onSkipTraced, onContactSaved, onModalToggle }) => {
  const dispatch = useDispatch();
  const { skipTrace, materializing } = useSkipTraceInvestors();
  const dialPhone = useDialInvestorPhone(investor, contactId);
  const contacts = investor.contacts || [];
  const mailingAddresses = investor.mailingAddresses || [];
  // ATAI-373: having *any* contacts isn't the same as having been skip
  // traced -- the Edit Contact modal lets a user add a phone/email by hand
  // with no skip trace involved at all (its own `source: 'MANUAL'`, see
  // ContactPerson/SourceHint above), and that used to be enough on its
  // own to flip this whole card into its "skip traced" look, hiding the
  // Skip Trace banner/button even though no skip trace had actually run.
  // Showing the contact list and the Skip Trace banner are no longer
  // mutually exclusive below: a manually-added contact is still shown, but
  // the banner stays up alongside it until a real (non-MANUAL-sourced)
  // contact exists.
  const hasContacts = Boolean(contacts.length);
  const hasSkipTracedContact = contacts.some(
    (contact) =>
      (contact.phones || []).some((phone) => phone.source !== 'MANUAL') ||
      (contact.emails || []).some((email) => email.source !== 'MANUAL')
  );

  // Edit Contact and Skip Trace each open their own stacked popup (both
  // already at Priority.HIGHEST — see EditContactModal.js / the
  // InvestorSkipTraceModal.open comment) on top of the Investor Profile
  // modal. Each is its own DOM subtree, not a descendant of Profile's own
  // ref, so SimpleModal's outside-click listener reads any click inside one
  // as "outside" Profile too and closes it — `onModalToggle` (threaded down
  // from Profile/index.js, same contract as PropertyTable's
  // `onPropertyDetailToggle` / useSaveInvestor's `onToggle`) suppresses that
  // while either is open.
  const handleEditContact = () => {
    onModalToggle?.(true);
    dispatch(
      EditContactModal.open({
        investor,
        contactId,
        onSaved: onContactSaved,
        onClose: () => onModalToggle?.(false),
      })
    );
  };

  return (
    <div className={css.contactCard}>
      <div className={css.contactCard__header}>
        <p className={css.contactCard__title}>Contact Information</p>
        <button type="button" className={css.editButton} onClick={handleEditContact}>
          <SVG icon="cp-edit" className={css.editButton__icon} />
          Edit
        </button>
      </div>

      {!hasContacts ? null : (
        <div className={css.contactList}>
          {contacts.map((contact, index) => (
            <ContactPerson
              key={contact.name || index}
              contact={contact}
              index={index}
              onPhoneClick={dialPhone}
            />
          ))}
        </div>
      )}

      {hasSkipTracedContact ? null : (
        <div className={css.contactBanner}>
          <div className={css.contactBanner__copy}>
            <p className={css.contactBanner__title}>Contact Information</p>
            <p className={css.contactBanner__body}>
              Access the contact information for this investor at no cost.
            </p>
          </div>
          <button
            type="button"
            className={css.skipTraceButton}
            disabled={materializing}
            onClick={() => skipTrace([investor], { onSuccess: onSkipTraced, onModalToggle })}
          >
            <SVG icon="iconInvestorSkipTrace" className={css.skipTraceButton__icon} />
            Skip Trace
          </button>
        </div>
      )}

      {!mailingAddresses.length ? null : (
        <div className={css.mailing}>
          <p className={css.mailing__title}>Mailing Address</p>
          {mailingAddresses.map((address, index) => (
            <p key={address.hash || address.fullAddress || index} className={css.mailing__address}>
              {formatMailingAddress(address)}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactInfo;
